import Button from 'components/shared/Buttons/BaseButton';
import Link from 'components/shared/Link/Link';
import LinkWithIcon from 'components/shared/LinkWithIcon/LinkWithIcon';
import { Cta, LinkedIconProps } from 'libs/contentful/types';
import { cn } from 'utils/styles';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import MenuClose from '../../public/icons/menu-close.svg';
import MenuBack from '../../public/icons/menu-back.svg';
import NavMenuButton from './NavMenuButton';
import { BUTTON_DURATION, SUB_MENU_DURATION } from './animations';
import { HEADER_NAV_ID_PREFIX, navTopBarProps } from 'components/MainNav/utils';
import CTA from 'components/shared/CTA/CTA';
import ChimeLogo from 'components/shared/ChimeLogo/ChimeLogo';

interface TopNavBarProps {
  topBarLink: LinkedIconProps;
  topBarCta: Cta;
  menuIsOpen: boolean;
  subMenuIsOpen: boolean;
  showShadows: boolean;
  logoHref: string;
  closeMenu: () => void;
  openMenu: () => void;
  closeSubMenu: () => void;
}

const TopNavBar = ({
  topBarLink,
  topBarCta,
  menuIsOpen,
  subMenuIsOpen,
  showShadows,
  logoHref,
  closeMenu,
  openMenu,
  closeSubMenu,
}: TopNavBarProps) => {
  // idSuffixes are used to ensure unique ids on navigable elements
  const mobileIdSuffix = 'small';
  const desktopIdSuffix = 'large';

  const loginLink = (idSuffix: string) => (
    <LinkWithIcon
      id={`${HEADER_NAV_ID_PREFIX}_log_in_${idSuffix}`}
      href={topBarLink.url}
      internalPage={topBarLink.internalPage}
      chimeKitIconName={topBarLink.iconName}
      icon={topBarLink?.icon}
      className="font-semibold font-sans text-label-sans active:scale-none"
      sys={topBarLink?.sys}>
      {topBarLink.label}
    </LinkWithIcon>
  );

  const menuButton = (idSuffix: string) => (
    <NavMenuButton
      subMenuIsOpen={subMenuIsOpen}
      menuIsOpen={menuIsOpen}
      openMenu={openMenu}
      closeMenu={closeMenu}
      closeSubMenu={closeSubMenu}
      idSuffix={idSuffix}
    />
  );

  const ctaButton = (idSuffix: string) => (
    <CTA cta={topBarCta} className="min-w-0 px-5" id={`${HEADER_NAV_ID_PREFIX}_enroll_link_${idSuffix}`} />
  );

  const homeLink = (idSuffix: string) => (
    <Link href={logoHref} id={`${HEADER_NAV_ID_PREFIX}_chime_logo_${idSuffix}`}>
      <span className={!menuIsOpen ? 'in-cw-action-primary:white-images' : ''}>
        <ChimeLogo alt="Chime Home" />
      </span>
    </Link>
  );

  const crossFadeIn = { opacity: [0, 1], transition: { duration: 0.3 } };
  const crossFadeOut = { opacity: [1, 0], transition: { duration: 0.3 } };

  const desktopNav = (
    <div className="sm:flex hidden justify-between h-full">
      {/* Desktop Left Section */}
      {/* Calculation is total width of desktop menu minus desktop menu left margin */}
      <div className="flex justify-between items-center w-[calc(400px-56px)]">
        <div className="flex gap-4 items-center">
          <div className="flex">{menuButton(desktopIdSuffix)}</div>
          {homeLink(desktopIdSuffix)}
        </div>

        <div className="mr-4">
          <AnimatePresence>
            {subMenuIsOpen && (
              <m.div
                animate={{ opacity: [0, 1], transition: { delay: SUB_MENU_DURATION, duration: BUTTON_DURATION } }}
                exit={{ opacity: [1, 0], transition: { duration: SUB_MENU_DURATION } }}
                className="flex items-center">
                {
                  <Button variant="icon" label="Close" onClick={closeMenu}>
                    <MenuClose />
                  </Button>
                }
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop Right Section */}
      <div className="flex gap-4 h-full">
        <div className="flex">{ctaButton(desktopIdSuffix)}</div>
        <div className="flex">{loginLink(desktopIdSuffix)}</div>
      </div>
    </div>
  );

  const mobileNav = (
    <div className="flex sm:hidden justify-between h-full">
      {/* Mobile Left Section */}
      <div className="grid grid-rows-1 grid-cols-1 min-w-16">
        <AnimatePresence>
          {subMenuIsOpen ? (
            <m.div
              animate={crossFadeIn}
              exit={crossFadeOut}
              className="flex row-start-1 col-start-1 justify-start"
              key="back-button">
              <Button
                variant="icon"
                label={'Back'}
                onClick={closeSubMenu}
                className="pl-0 active:scale-none justify-start">
                <MenuBack />
              </Button>
            </m.div>
          ) : (
            <m.div
              animate={crossFadeIn}
              exit={crossFadeOut}
              className="flex row-start-1 col-start-1"
              key="mobile-home-link">
              {homeLink(mobileIdSuffix)}
            </m.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Right Section */}
      <div className="flex gap-4 h-full">
        {/* Grid display makes content overlap on animation */}
        <div className="grid grid-rows-1 grid-cols-1">
          <AnimatePresence>
            {menuIsOpen ? (
              <m.div
                className="flex row-start-1 col-start-1 justify-end"
                key="login-link"
                animate={crossFadeIn}
                exit={crossFadeOut}>
                {loginLink(mobileIdSuffix)}
              </m.div>
            ) : (
              <m.div
                className="flex row-start-1 col-start-1 "
                key="cta-button"
                animate={crossFadeIn}
                exit={crossFadeOut}>
                {ctaButton(mobileIdSuffix)}
              </m.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex">{menuButton(mobileIdSuffix)}</div>
      </div>
    </div>
  );

  return (
    <div
      {...navTopBarProps}
      className={cn('w-full relative h-16 sm:h-18 z-50', showShadows && 'shadow-[10px_10px_20px] shadow-kale/5 z-100')}>
      <div className="px-6 sm:px-14 h-full">
        <div className="py-4 h-full">
          {mobileNav}
          {desktopNav}
        </div>
      </div>
    </div>
  );
};

export default TopNavBar;
